import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Package, Loader2, CheckCircle2, Circle, Eye } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  pendente:      { label: "PENDENTE",      cls: "bg-amber-100 text-amber-800 border-amber-300" },
  em_separacao:  { label: "EM SEPARAÇÃO",  cls: "bg-blue-100 text-blue-800 border-blue-300" },
  separado:      { label: "SEPARADO",      cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  entregue:      { label: "ENTREGUE",      cls: "bg-slate-200 text-slate-800 border-slate-300" },
  cancelada:     { label: "CANCELADA",     cls: "bg-red-100 text-red-700 border-red-300" },
};

const fmtData = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return iso; }
};

function DetalheSep({ open, onOpenChange, sid, onAtualizado }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);

  const carregar = useCallback(async () => {
    if (!sid) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/posvenda/separacoes/${sid}`);
      setD(data);
    } catch (e) { toast.error("Erro ao carregar"); }
    finally { setLoading(false); }
  }, [sid]);

  useEffect(() => { if (open) carregar(); }, [open, carregar]);

  const toggleItem = async (idx) => {
    try {
      await api.patch(`/posvenda/separacoes/${sid}/itens/${idx}`);
      await carregar();
      onAtualizado && onAtualizado();
    } catch (e) { toast.error("Erro"); }
  };

  const avancar = async (novo) => {
    try {
      await api.patch(`/posvenda/separacoes/${sid}/status`, { novo_status: novo });
      toast.success(`Status: ${novo}`);
      await carregar();
      onAtualizado && onAtualizado();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="detalhe-sep-modal">
        {loading || !d ? (
          <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <Package className="w-5 h-5 text-orange-600" />
                Separação · OS {d.os_numero}
                <Badge className={`${STATUS[d.status]?.cls || ""} border text-[10px]`}>
                  {STATUS[d.status]?.label || d.status}
                </Badge>
              </DialogTitle>
              <DialogDescription className="text-xs">
                Cliente: <strong>{d.cliente_nome}</strong> · Técnico: <strong>{d.tecnico_nome || "—"}</strong> ·
                Execução: <strong>{fmtData(d.data_execucao)}</strong>
              </DialogDescription>
            </DialogHeader>

            <Card className="p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
                Itens a separar ({(d.itens || []).filter(i => i.separado).length}/{(d.itens || []).length})
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12"></TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead className="w-24 text-center">Qtd</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(d.itens || []).map((it, idx) => (
                    <TableRow key={idx}
                      className={it.separado ? "bg-emerald-50" : ""}
                      data-testid={`sep-row-${idx}`}>
                      <TableCell>
                        <button onClick={() => toggleItem(idx)}
                          className="hover:scale-110 transition"
                          data-testid={`sep-toggle-${idx}`}
                          disabled={d.status === "entregue" || d.status === "cancelada"}>
                          {it.separado ? (
                            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                          ) : (
                            <Circle className="w-5 h-5 text-slate-300" />
                          )}
                        </button>
                      </TableCell>
                      <TableCell>
                        <div className={`text-sm ${it.separado ? "line-through text-slate-500" : ""}`}>
                          {it.descricao}
                        </div>
                        <div className="text-[10px] font-mono text-slate-500">{it.codigo}</div>
                      </TableCell>
                      <TableCell className="text-center text-sm">{it.quantidade} {it.unidade}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>

            <DialogFooter className="flex-wrap gap-2">
              {d.status === "pendente" && (
                <Button onClick={() => avancar("em_separacao")} data-testid="btn-iniciar-sep"
                  className="bg-blue-600 hover:bg-blue-700 text-white">
                  Iniciar separação
                </Button>
              )}
              {d.status === "separado" && (
                <Button onClick={() => avancar("entregue")} data-testid="btn-marcar-entregue"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Marcar como entregue
                </Button>
              )}
              <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function PosvendaSeparacoes() {
  const [items, setItems] = useState([]);
  const [resumo, setResumo] = useState({});
  const [loading, setLoading] = useState(false);
  const [filtro, setFiltro] = useState("todos");
  const [det, setDet] = useState({ open: false, sid: null });

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtro !== "todos") params.status = filtro;
      const { data } = await api.get("/posvenda/separacoes", { params });
      setItems(data.items || []);
      setResumo(data.resumo || {});
    } catch (e) { toast.error("Erro ao listar"); }
    finally { setLoading(false); }
  }, [filtro]);

  useEffect(() => { carregar(); }, [carregar]);

  return (
    <div className="space-y-4" data-testid="posvenda-sep-page">
      <div>
        <h1 className="text-2xl font-light text-slate-900 flex items-center gap-2">
          <Package className="w-6 h-6 text-orange-600" />
          Separações de Peças
        </h1>
        <p className="text-xs text-slate-500 mt-1">
          Pedidos de peças gerados automaticamente ao criar uma OS · Operador/Oficina
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {Object.entries(STATUS).map(([k, v]) => (
          <Card key={k} className="p-3 cursor-pointer hover:shadow-md transition"
            onClick={() => setFiltro(k === filtro ? "todos" : k)}
            data-testid={`kpi-sep-${k}`}>
            <div className="text-[10px] uppercase text-slate-500 tracking-wider">{v.label}</div>
            <div className="text-2xl font-light text-slate-900">{resumo[k] || 0}</div>
          </Card>
        ))}
      </div>

      <Card className="p-4">
        <Tabs value={filtro} onValueChange={setFiltro}>
          <TabsList className="mb-3">
            <TabsTrigger value="todos">Todas</TabsTrigger>
            <TabsTrigger value="pendente">Pendentes</TabsTrigger>
            <TabsTrigger value="em_separacao">Em Separação</TabsTrigger>
            <TabsTrigger value="separado">Separadas</TabsTrigger>
            <TabsTrigger value="entregue">Entregues</TabsTrigger>
          </TabsList>
        </Tabs>

        {loading ? (
          <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-slate-500 text-sm">Nenhuma separação encontrada.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-32">OS</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead className="w-32">Técnico</TableHead>
                <TableHead className="w-28">Execução</TableHead>
                <TableHead className="w-24 text-center">Itens</TableHead>
                <TableHead className="w-32">Status</TableHead>
                <TableHead className="w-16 text-right">Ação</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((s) => {
                const st = STATUS[s.status] || { label: s.status, cls: "bg-slate-100" };
                return (
                  <TableRow key={s.id} data-testid={`row-sep-${s.id}`}>
                    <TableCell className="font-mono text-xs">{s.os_numero}</TableCell>
                    <TableCell className="text-sm">{s.cliente_nome}</TableCell>
                    <TableCell className="text-xs">{s.tecnico_nome || "—"}</TableCell>
                    <TableCell className="text-xs">{fmtData(s.data_execucao)}</TableCell>
                    <TableCell className="text-center text-xs">
                      <span className={s.itens_separados === s.total_itens && s.total_itens > 0
                        ? "text-emerald-700 font-medium" : "text-slate-700"}>
                        {s.itens_separados}/{s.total_itens}
                      </span>
                    </TableCell>
                    <TableCell><Badge className={`${st.cls} border text-[10px] font-mono`}>{st.label}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost"
                        onClick={() => setDet({ open: true, sid: s.id })}
                        data-testid={`btn-ver-sep-${s.id}`}>
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      <DetalheSep open={det.open} onOpenChange={(o) => setDet((d) => ({ ...d, open: o }))}
        sid={det.sid} onAtualizado={carregar} />
    </div>
  );
}
